import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Truck } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const JobMode = () => {
  // Get session data
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // State
  const [jobModes, setJobModes] = useState([]);
  const [filteredJobModes, setFilteredJobModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Form data (exact SQL Server field names)
  const [formData, setFormData] = useState({
    JobModeID: null,
    JobMode: ''
  });
  
  const [errors, setErrors] = useState({});

  // Load data on mount
  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchJobModes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterJobModes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, jobModes]);

  const fetchJobModes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/JobMode.php');
      
      if (response.data.success) {
        setJobModes(response.data.data || []);
      } else {
        alert('Failed to fetch job modes: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching job modes:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching job modes.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterJobModes = () => {
    if (!searchTerm.trim()) {
      setFilteredJobModes(jobModes);
    } else {
      const filtered = jobModes.filter(jm =>
        jm.JobMode.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredJobModes(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.JobMode.trim()) {
      newErrors.JobMode = 'Job mode is required';
    } else if (formData.JobMode.length > 50) {
      newErrors.JobMode = 'Job mode must be 50 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/JobMode.php', formData);
      
      if (response.data.success) {
        alert(editMode ? 'Job mode updated successfully!' : 'Job mode created successfully!');
        setShowModal(false);
        resetForm();
        fetchJobModes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving job mode:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving job mode: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (jobMode) => {
    setFormData({
      JobModeID: jobMode.JobModeID,
      JobMode: jobMode.JobMode
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job mode? This may affect existing job types.')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/JobMode.php', {
        data: { JobModeID: id }
      });
      
      if (response.data.success) {
        alert('Job mode deleted successfully!');
        fetchJobModes();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting job mode:', error);
      alert('Error deleting job mode: ' + (error.response?.data?.message || 'Please try again.'));
    }
  };

  const resetForm = () => {
    setFormData({
      JobModeID: null,
      JobMode: ''
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Check session context
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Job Mode Master</h2>
          <p className="subtitle">Manage job operation modes</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Job Mode
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search job modes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredJobModes.length} {filteredJobModes.length === 1 ? 'job mode' : 'job modes'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading job modes...</div>
      ) : filteredJobModes.length === 0 ? (
        <div className="empty-state">
          <p>No job modes found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Job Mode</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobModes.map((jobMode) => (
                <tr key={jobMode.JobModeID}>
                  <td className="font-mono">{jobMode.JobModeID}</td>
                  <td className="font-semibold">{jobMode.JobMode}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(jobMode)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(jobMode.JobModeID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Job Mode' : 'Add New Job Mode'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>
                  <Truck size={18} />
                  Job Mode Information
                </h4>
                
                <div className="form-group">
                  <label htmlFor="JobMode">
                    Job Mode <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="JobMode"
                    value={formData.JobMode}
                    onChange={(e) => setFormData({ ...formData, JobMode: e.target.value })}
                    maxLength="50"
                    placeholder="e.g., Import, Export, Domestic, Transshipment"
                    className={errors.JobMode ? 'error' : ''}
                    autoFocus
                  />
                  {errors.JobMode && (
                    <span className="error-message">{errors.JobMode}</span>
                  )}
                  <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    Enter the job operation mode (e.g., Import, Export, Domestic)
                  </span>
                </div>

                <div style={{ 
                  marginTop: '20px', 
                  padding: '12px', 
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px'
                }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#0369a1' }}>
                    💡 <strong>Common Job Modes:</strong> Import, Export, Domestic, Transshipment, Cross Trade
                  </p>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMode;